import { useState, useEffect } from "react";
import axios from "axios";

import {
  EyeIcon,
  FileIcon,
  FileJpgIcon,
  FilePdfIcon,
  FilePngIcon,
} from "@phosphor-icons/react";

import { VACATION_REQUESTS_STRINGS } from "../constants/strings";
import { FilePreviewDialog } from "../../../../../components/v2";
import { Toast } from "../../../../../components/toast";

import { formatBytes } from "../../../../../shared/helpers/file";

const FILE_ICON = Object.freeze({
  pdf: <FilePdfIcon size={32} color="#003A70" />,
  png: <FilePngIcon size={32} color="#003A70" />,
  jpg: <FileJpgIcon size={32} color="#003A70" />,
  jpeg: <FileJpgIcon size={32} color="#003A70" />,
  default: <FileIcon size={32} color="#003A70" />,
});

const getFileExtension = (fileName) => {
  const parts = fileName.split(".");
  return parts.length > 1 ? parts.pop().toLowerCase() : "";
};

const getFileIcon = (fileName) => {
  const extension = getFileExtension(fileName);

  if (!extension) return FILE_ICON.default;

  return FILE_ICON[extension] || FILE_ICON.default;
};

type FileItemPropsT = {
  file: {
    fileName: string;
    key: string;
    url: string;
    size: number;
  };
};

const FileItem = ({ file }: FileItemPropsT) => {
  const [showPreview, setShowPreview] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);
  const [isFetching, setIsFetching] = useState(false);

  const isPdf = getFileExtension(file.fileName) === "pdf";

  useEffect(() => {
    return () => {
      if (blobUrl) window.URL.revokeObjectURL(blobUrl);
    };
  }, [blobUrl]);

  // Pre-fetches the file via a CORS request so the CDN caches the response
  // with CORS headers, preventing intermittent CORS errors on image downloads.
  const fetchBlob = async () => {
    if (blobUrl) return blobUrl;

    setIsFetching(true);

    try {
      const response = await axios.get(file.url, { responseType: "blob" });
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);

      setBlobUrl(url);

      return url;
    } catch (error) {
      Toast(
        "error",
        VACATION_REQUESTS_STRINGS.FILES_DOWNLOAD_ERROR_TITLE,
        error.message,
      );
      return null;
    } finally {
      setIsFetching(false);
    }
  };

  const showFilePreview = async () => {
    if (isFetching) return;

    const url = await fetchBlob();

    if (url) setShowPreview(true);
  };

  const handleOnClose = () => {
    if (!showPreview) return;

    setShowPreview(false);
  };

  const handleDownload = () => {
    if (!blobUrl) {
      Toast("error", VACATION_REQUESTS_STRINGS.FILES_DOWNLOAD_ERROR_TITLE);

      return;
    }

    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = file.fileName;

    document.body.appendChild(link);

    link.click();
    link.remove();
  };

  return (
    <li className="flex bg-gray-100 rounded-lg p-2 gap-2 items-center">
      <div
        role="img"
        aria-label="Ícono de archivo adjunto"
        className="bg-white p-1 rounded-xl"
      >
        {getFileIcon(file.fileName)}
      </div>

      <dl className="flex flex-col flex-1 min-w-0">
        <dt className="sr-only">Nombre</dt>
        <dd title={file.fileName} className="truncate font-medium">
          {file.fileName}
        </dd>

        {!!file.size && (
          <>
            <dt className="sr-only">Tamaño</dt>
            <dd className="text-secondaryText text-sm">
              {formatBytes(file.size)}
            </dd>
          </>
        )}
      </dl>

      <button
        type="button"
        aria-label={`${VACATION_REQUESTS_STRINGS.FILES_VIEW_BUTTON_LABEL}: ${file.fileName}`}
        className="h-12 w-12 shrink-0 p-1 rounded-full hover:bg-gray-200 transition-colors flex items-center justify-center"
        disabled={isFetching}
        onClick={showFilePreview}
      >
        <EyeIcon aria-hidden="true" size={20} />
      </button>

      <FilePreviewDialog
        fileName={file.fileName}
        url={blobUrl}
        isPdf={isPdf}
        visible={showPreview}
        onHide={handleOnClose}
        onDownload={handleDownload}
        downloadLabel={VACATION_REQUESTS_STRINGS.FILES_DOWNLOAD_BUTTON_LABEL}
      />
    </li>
  );
};

type FilesSectionPropsT = {
  files: {
    fileName: string;
    key: string;
    url: string;
    size: number;
  }[];
};

const FilesSection = ({ files }: FilesSectionPropsT) => {
  if (!files?.length) return null;

  return (
    <section
      aria-label={VACATION_REQUESTS_STRINGS.FILES_SECTION_TITLE}
      className="flex flex-col gap-1"
    >
      <h3>{VACATION_REQUESTS_STRINGS.FILES_SECTION_TITLE}</h3>
      <ul className="flex flex-col gap-1">
        {files.map((file) => (
          <FileItem key={file.key} file={file} />
        ))}
      </ul>
    </section>
  );
};

export default FilesSection;
